/* eslint-disable camelcase */
import moment from 'moment'
import {
  Button as MuiButton,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid as MuiGrid,
  IconButton,
  TextField as MuiTextField,
  Typography as MuiTypography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
  Add as AddIcon,
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@material-ui/icons'
import { Autocomplete } from '@material-ui/lab'
import { spacing } from '@material-ui/system'
import { AxiosError, AxiosResponse } from 'axios'
import { plainToClass } from 'class-transformer'
import { DropzoneAreaBase } from 'material-ui-dropzone'
import React, { createRef, ReactNode, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import {
  ASN,
  ParcelOrder,
  OrderStatusEnum,
  ParcelOrderUnit,
  Sku,
} from 'src/api/models'
import { AttachmentProp } from 'src/components/Dialog/AddAttachmentsDialog'
import { FilePreview, UrlPreview } from 'src/components/FilePreview'
import { useQueryPaginator } from 'src/components/Table'
import { Notification } from 'src/components/Notification'
import {
  useForm,
  AddressField,
  BooleanField,
  DateField,
  GenerateChipDisplay,
  MapSelectField,
  NumberField,
  QueryMultiSelectField,
  QuerySelectField,
  TextareaField,
  TextField,
} from 'src/forms'
import { setApiErrors } from 'src/forms/errors'
import { skuit } from 'src/services/skuit'
import { handleErrorMessage } from 'src/components/Notification/utils'
import styled from 'styled-components'
import * as yup from 'yup'
import { FlagDialog } from './FlagDialog'
import { useMutation } from 'react-query'

const Centered = styled.div`
  text-align: center;
`

const Button = styled(MuiButton)(spacing)
const Grid = styled(MuiGrid)(spacing)
const Typography = styled(MuiTypography)(spacing)

const useStyles = makeStyles(() => ({
  form: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
}))

interface CustomFieldProps {
  name: string
  value: string
}

interface SkuProps {
  sku: string
  num_units: number // eslint-disable-line camelcase
}

interface AddressProps {
  company: string
  consignee: string
  phone: string
  address1: string
  address2: string
  address3: string
  city: string
  state: string
  postal_code: string // eslint-disable-line camelcase
}

interface OrderProps {
  client?: string
  carrier?: string
  number: string
  details: string
  fulfill_on: string
  status: string
  address: AddressProps
  custom_fields: CustomFieldProps[] // eslint-disable-line camelcase
  inventory: SkuProps[]
  attachments?: AttachmentProp[]
}

const orderSchema = yup.object().shape({
  client: yup.string().label('Client'),
  carrier: yup.string().label('Carrier').optional(),
  number: yup
    .string()
    .nullable()
    .label('.Order Number')
    .transform((value) => (value === '' ? undefined : value)),
  fulfill_on: yup
    .date()
    .optional()
    .nullable()
    .default(null),
  details: yup
    .string()
    .optional()
    .nullable()
    .label('Order Details')
    .transform((value) => (value === '' ? undefined : value)),
  address: yup
    .object({
      company: yup.string().label('Company').optional(),
      consignee: yup.string().label('Consignee').required(),
      phone: yup.string().label('Phone Number').optional().nullable(),
      address1: yup.string().label('Address 1').required(),
      address2: yup.string().label('Address 2'),
      address3: yup.string().label('Address 3'),
      city: yup.string().label('City').required(),
      state: yup.string().label('State').required(),
      postal_code: yup.string().label('Postal Code').required(),
    })
    .required(),
  status: yup.string().label('Status').required(),
  flags: yup.array().of(yup.string()),
  attachments: yup.array().of(
    yup
      .object({
        xid: yup.string().when('file', {
          is: (file) => !file,
          then: yup.string().required(),
        }),
        file: yup.mixed(),
        tags: yup.array().of(yup.string()),
      })
      .label('Attachments')
  ),
  inventory: yup.array().of(
    yup
      .object({
        sku: yup.string().required().label('SKU'),
        num_units: yup
          .number()
          .required()
          .label('Number of Units')
          .transform((currentValue, originalValue) => {
            if (currentValue === '' || originalValue === '') {
              return undefined
            }
            return currentValue
          }),
      })
      .label('Inventory')
  ),
})

interface ImageListWrapperProps {
  children: ReactNode
}

const ImageListWrapper = (props: ImageListWrapperProps) => {
  const { children } = props
  return (
    <Grid container spacing={6} mt={6}>
      {children}
    </Grid>
  )
}

export interface FlagDialogValues {
  name?: string
  updateValues?: (value: any) => void
}

interface Props {
  open: boolean
  handleClose: () => void
  onSave?: () => void
  update?: boolean
  order?: ParcelOrder
  asn?: ASN
}

export const ParcelOrderDialog = (props: Props) => {
  const { open, handleClose, onSave, update, order, asn } = props

  const classes = useStyles()

  const [saving, setSaving] = useState(false)
  const [clientGuid, setClientGuid] = useState<string | null>(
    order?.client?.guid || asn?.client?.guid || null
  )
  const [inventoryOpen, setInventoryOpen] = useState(true)

  const clientPaginator = useQueryPaginator('client-list', skuit.sc.clients.list, {
    params: { search: order?.client?.name || asn?.client?.name },
  })
  const carrierPaginator = useQueryPaginator('carrier-list', skuit.sc.carriers.list)
  const facilityPaginator = useQueryPaginator('facility-list', skuit.sc.facilities.list)
  const skuPaginator = useQueryPaginator('sku-list', skuit.sc.skus.list, {
    params: { active: 'true' },
  })
  const customFieldPaginator = useQueryPaginator('custom-fields', skuit.sc.parcel.orders.customFields.list, {
    params: { clients: order?.client?.guid || 'ac_noac' },
  })
  const customFieldValues: Record<string, string> = {}
  if (order?.customFields?.length) {
    for (const field of order.customFields) {
      customFieldValues[field.name || ''] = field.value || ''
    }
  }

  const orderUnitPaginator = useQueryPaginator('order-unit-list', skuit.sc.parcel.orders.units.list, {
    params: { orders: order?.guid || 'or_noorder', 'on-container': 'false' },
  })

  const tagPaginator = useQueryPaginator('tag-list', skuit.sc.attachments.tags.list)
  const flagPaginator = useQueryPaginator('flag-list', skuit.sc.parcel.orders.flags.list)

  const { Form, Field, FieldList, data: formData } = useForm(orderSchema)

  const patchSkuFilters = skuPaginator.patchFilters
  const patchCustomFieldFilters = customFieldPaginator.patchFilters
  const setFormData = formData.setValue
  useEffect(() => {
    if (clientGuid) {
      patchSkuFilters({ clients: clientGuid })
      patchCustomFieldFilters({ clients: clientGuid })
      if (!order && !asn) {
        setFormData('inventory', [])
      }
    }
  }, [asn, order, clientGuid, patchSkuFilters, setFormData, patchCustomFieldFilters])

  const skuFileInput = createRef<HTMLInputElement>()

  const { mutate: submitSkus } = useMutation(skuit.sc.parcel.orders.skuAvailability, {
    onSuccess: (response: AxiosResponse) => {
      const newInventory = response.data.map(
        (field: { sku: Sku; num_units: number; lookup: string }, index: number) => {
          // eslint-disable-line camelcase
          const { sku, num_units, lookup } = field // eslint-disable-line camelcase
          const skuObj = plainToClass(Sku, sku)
          const existing = formData.getValue('inventory')
          if (!skuObj) {
            formData.setError(
              `inventory[${index + existing.length}].sku`,
              `Invalid SKU lookup: ${lookup}`
            )
          } else if (num_units > (skuObj.numAvailable || 0)) {
            formData.setError(
              `inventory[${index + existing.length}].num_units`,
              'Not enough available units'
            )
          }
          return { sku: skuObj, num_units }
        }
      )
      formData.extend('inventory', newInventory)
    },
    onError: (error: AxiosError) => {
      if (skuFileInput.current) {
        skuFileInput.current.value = ''
      }
      if (error?.response?.data?.details) {
        toast.error(() => (
          <Notification message={`Failed to process CSV: ${error?.response?.data.details}`} />
        ))
      } else {
        toast.error(() => <Notification message={`Failed to process CSV: ${error}`} />)
      }
    },
  })

  const onSkuFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const form = new FormData()
      form.append('file', e.target.files[0])
      let params: any
      if (order?.client?.guid) {
        params = { client: order.client.guid }
      }
      submitSkus(form, params)
    }
  }

  const startingInventory = orderUnitPaginator.data.count
    ? orderUnitPaginator.data.results.map((item: ParcelOrderUnit) => ({
        sku: item.sku,
        num_units: item.numUnits,
      }))
    : undefined

  const onSubmit = (data: OrderProps) => {
    setSaving(true)
    const attachments = data.attachments
    delete data.attachments

    const customFields = []
    for (const field of customFieldPaginator.data.results) {
      if (field.name in data) {
        customFields.push({ name: field.name, value: (data as any)[field.name] })
        delete (data as any)[field.name]
      }
    }
    if (customFields.length) {
      data.custom_fields = customFields
    }

    if (!data.inventory) {
      data.inventory = []
    }

    if (data.fulfill_on) {
      const fulfillOn = moment(data.fulfill_on)
      data.fulfill_on = fulfillOn.format("YYYY-MM-DD")
    }

    let func = skuit.sc.parcel.orders.create
    let action = 'creat'
    if (update) {
      func = (data: OrderProps) => skuit.sc.parcel.orders.patch(order?.guid || '', data)
      action = 'updat'
      delete data.client
    }
    func(data)
      .then((newOrder: ParcelOrder) => {
        if (attachments?.length) {
          skuit.sc.parcel.orders.attachments
            .create(newOrder.guid as string, { attachments })
            .then(() => {
              handleClose()
              if (onSave) {
                onSave()
              }
              toast.success(() => (
                <Notification
                  message={`Successfully ${action}ed Order: ${newOrder.name}`}
                  newObject={newOrder}
                />
              ))
              toast.success(() => (
                <Notification message={`Successfully added ${attachments.length} attachments!`} />
              ))
            })
            .catch((attachmentError: AxiosError) => {
              handleErrorMessage(attachmentError, action, 'Attachments')
              setApiErrors(formData.setError, attachmentError)
            })
        } else {
          handleClose()
          if (onSave) {
            onSave()
          }
          toast.success(() => (
            <Notification
              message={`Successfully ${action}ed Order: ${newOrder.name}`}
              newObject={newOrder}
            />
          ))
        }
      })
      .catch((error: AxiosError) => {
        handleErrorMessage(error, action, 'Order')
        setApiErrors(formData.setError, error)
      })
      .finally(() => {
        setSaving(false)
      })
  }
  let saveLabel: string
  if (orderUnitPaginator.loading) {
    saveLabel = 'Loading...'
  } else if (saving) {
    saveLabel = 'Saving...'
  } else {
    saveLabel = 'Save'
  }

  const isInventoryDisabled =
    order?.status === 'processed' ||
    order?.status === 'shipped' ||
    order?.status === 'delivered' ||
    order?.status === 'canceled'

  const isOnlyStatusChanges =
    order?.status === 'shipped' || order?.status === 'delivered' || order?.status === 'canceled'

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Form formProps={{ className: classes.form }} onSubmit={onSubmit}>
        <DialogTitle>{order ? 'Update' : 'Create'} Order</DialogTitle>
        <DialogContent>
          <QuerySelectField
            name="client"
            label="Client"
            paginator={clientPaginator}
            defaultValue={order?.client || asn?.client}
            disabled={update || !!asn || isOnlyStatusChanges}
            onSelected={(client) => setClientGuid(client.guid)}
          />
          <QuerySelectField
            name="facility"
            label="Facility"
            paginator={facilityPaginator}
            defaultValue={order?.facility || asn?.facility}
            disabled={isOnlyStatusChanges}
          />
          <QuerySelectField
            name="carrier"
            label="Carrier"
            paginator={carrierPaginator}
            defaultValue={order ? order.carrier : undefined}
            disabled={isOnlyStatusChanges}
            getOptionLabel={(option) => `${option.name}${option.scac ? ' [' + option.scac + ']' : ''}`}
            renderOption={(option) => (
              <span>
                {option.name}
                {option.scac && ` [${option.scac}]`}
              </span>
            )}
          />
          <TextField
            name="number"
            label="Order Number"
            defaultValue={order?.number}
            disabled={!clientGuid || isOnlyStatusChanges}
          />
          <DateField
            name="fulfill_on"
            label="Date to Fulfill On"
            defaultValue={order?.fulfillOn}
            disabled={!clientGuid || isOnlyStatusChanges}
          />
          <TextareaField
            name="details"
            label="Order Details"
            defaultValue={order?.details}
            disabled={!clientGuid || isOnlyStatusChanges}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <MapSelectField
                name="status"
                label="Status"
                options={ParcelOrder.Status}
                defaultValue={order?.status || OrderStatusEnum.received}
              />
            </Grid>
            <Grid item xs={6}>
              <QueryMultiSelectField
                name="flags"
                label="Flags"
                paginator={flagPaginator}
                defaultValue={order?.flags}
                disabled={isOnlyStatusChanges}
                renderSelected={GenerateChipDisplay(['name'])}
                CreateDialog={FlagDialog}
              />
            </Grid>
          </Grid>
          <Typography variant="h6" align="center" mt={12}>
            Shipping
          </Typography>
          <AddressField
            name="address"
            defaultValue={order?.address}
            disabled={!clientGuid || isOnlyStatusChanges}
          />
          {customFieldPaginator.data.results.map((field) => (
            <TextField
              key={field.name}
              name={field.name}
              label={field.label}
              defaultValue={customFieldValues[field.name]}
              disabled={isOnlyStatusChanges}
            />
          ))}
          <Typography variant="h6" mt={6} mb={3}>
            Attachments
          </Typography>
          <FieldList
            name="attachments"
            startCount={0}
            renderHeader={({ name, value, setValue }) =>
              order?.status === 'canceled' ? null : (
                <DropzoneAreaBase
                  dropzoneText="Drag and drop or click to add files"
                  fileObjects={[]}
                  onAdd={(files) => {
                    const fileData = files.map((fileObj) => {
                      return { file: fileObj.file, tags: [] }
                    })
                    const newValue = (value || []).concat(fileData)
                    setValue(name, newValue)
                  }}
                  filesLimit={10}
                  maxFileSize={75000000}
                  showPreviewsInDropzone={false}
                />
              )
            }
            renderList={ImageListWrapper}
            renderField={({ name: itemName }) => (
              <Grid item xs={3} key={itemName}>
                <Card>
                  <CardContent>
                    <Field
                      name="file"
                      renderField={({ value, remove, error }) => {
                        if (!value) {
                          return null
                        }
                        return (
                          <>
                            <FilePreview file={value} onDelete={() => remove(itemName)} />
                            {error && <FormHelperText error={!!error}>{error}</FormHelperText>}
                          </>
                        )
                      }}
                    />
                    <Field
                      name="url"
                      renderField={({ value, remove, error }) => {
                        if (!value) {
                          return null
                        }
                        return (
                          <>
                            <UrlPreview
                              url={value}
                              allowDownload={false}
                              onDelete={() => remove(itemName)}
                            />
                            {error && <FormHelperText error={!!error}>{error}</FormHelperText>}
                          </>
                        )
                      }}
                    />
                    <Field
                      name="tags"
                      getSubmitValue={(value) =>
                        value && value.map((tag: { slug: string }) => tag.slug)
                      }
                      renderField={({ name, value, setValue, error }) => (
                        <Autocomplete
                          disabled={isOnlyStatusChanges}
                          id={name}
                          freeSolo
                          options={tagPaginator.data.results}
                          getOptionLabel={(tag) => tag.name}
                          getOptionSelected={(option, value) => option.guid === value?.guid}
                          onChange={(_: any, newValue: any | null) => setValue(name, newValue)}
                          value={value}
                          renderInput={(params) => (
                            <MuiTextField
                              {...params}
                              error={!!error}
                              helperText={error}
                              label="Tags"
                            />
                          )}
                        />
                      )}
                    />
                  </CardContent>
                </Card>
              </Grid>
            )}
          />

          <Typography variant="h6" mt={12}>
            Inventory{' '}
            <IconButton onClick={() => setInventoryOpen(!inventoryOpen)}>
              {inventoryOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Typography>
          <Collapse in={inventoryOpen}>
            {orderUnitPaginator.loading ? (
              <CircularProgress color="inherit" />
            ) : (
              <FieldList
                name="inventory"
                startCount={0}
                defaultValues={startingInventory}
                renderField={({ name: listItemName, data }) => (
                  <Grid container spacing={2} key={listItemName}>
                    <Grid item xs={5}>
                      <QuerySelectField
                        name="sku"
                        label="[SKU] Product"
                        paginator={skuPaginator}
                        getOptionLabel={(sku) => `|${sku?.sku}| ${sku?.product?.name}`}
                        renderOption={(option) => {
                          return (
                            <Grid container spacing={2} m={2}>
                              <Grid item xs={12}>
                                <Typography>SKU: {option?.sku}</Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography>Product: {option?.product?.name}</Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography>
                                  Number Available: {option?.numAvailable}
                                </Typography>
                              </Grid>
                            </Grid>
                          )
                        }}
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <NumberField
                        name="num_units"
                        label="Number of Units"
                        disabled={!clientGuid || isInventoryDisabled}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <BooleanField
                        name="backorder"
                        label="Backorder"
                        disabled={!clientGuid || isInventoryDisabled}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <Button
                        disabled={isInventoryDisabled}
                        onClick={() => {
                          data.remove(listItemName)
                        }}
                      >
                        <CloseIcon /> Delete
                      </Button>
                    </Grid>
                  </Grid>
                )}
              />
            )}
            <Centered>
              <Grid container justify="center" spacing={6}>
                <Grid item>
                  <Button
                    disabled={!clientGuid || isInventoryDisabled}
                    variant="contained"
                    color="primary"
                    mt={6}
                    onClick={() =>
                      formData.append('inventory', { sku: undefined, num_units: undefined })
                    }
                  >
                    <AddIcon />
                    Add SKUs
                  </Button>
                </Grid>
                <Grid item>
                  <input
                    type="file"
                    accept=".csv"
                    ref={skuFileInput}
                    style={{ display: 'none' }}
                    onChange={onSkuFileSelected}
                  />
                  <Button
                    disabled={!clientGuid || isInventoryDisabled}
                    variant="contained"
                    color="primary"
                    mt={6}
                    onClick={() => {
                      if (skuFileInput.current) {
                        skuFileInput.current.click()
                      }
                    }}
                  >
                    <AddIcon />
                    Add SKUs by CSV
                  </Button>
                </Grid>
              </Grid>
            </Centered>
          </Collapse>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={
              saving ||
              orderUnitPaginator.loading
            }
            autoFocus
          >
            {saveLabel}
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  )
}
